import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../api/client';
import { REWARD_ICONS, useSettings, type RewardIcon } from '../settings/SettingsContext';
import { canSpeak, speak, stopSpeaking } from './speak';
import { playCorrectPing } from './ping';
import { endSession, fetchRewardSummary, type RewardSummary } from '../rewards/rewards';
import { ORDER_SEPARATOR, registerTrial, respondTrial } from './trials';
import { KINDS } from '../concepts/kinds';
import type { ProgramView } from '../programs/types';

type Sequence = { label: string; items: string[] };

type Props = {
  programId: string;
  learnerId?: string;
};

const FEEDBACK_HOLD_MS = 700;

function shuffle<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function gridClass(n: number): string {
  if (n <= 4) return 'cols-2';
  if (n <= 9) return 'cols-3';
  return 'cols-4';
}

export default function SequenceTap({ programId, learnerId }: Props) {
  const { settings } = useSettings();
  const [sequences, setSequences] = useState<Sequence[] | null>(null);
  const [promptMode, setPromptMode] = useState<'AUDIO' | 'TEXT'>('AUDIO');
  const [seq, setSeq] = useState<Sequence | null>(null);
  const [trialId, setTrialId] = useState<string | null>(null);
  /** Positions on the board, in the order they are laid out. */
  const [order, setOrder] = useState<number[]>([]);
  const [tapped, setTapped] = useState<number[]>([]);
  const [step, setStep] = useState(0);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [onBreak, setOnBreak] = useState(false);
  const [collected, setCollected] = useState<RewardSummary | null>(null);
  const [earnedNow, setEarnedNow] = useState(0);
  const [fadingOut, setFadingOut] = useState(false);
  const sessionIdRef = useRef<string>(crypto.randomUUID());
  const answeredRef = useRef(false);
  const playRef = useRef<() => void>(() => {});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const p = await api<ProgramView>(`/programs/${programId}`, { token: true });
        if (cancelled) return;
        const raw = (p.config?.sequences ?? []) as Sequence[];
        const usable = raw.filter((s) => s && s.label && Array.isArray(s.items) && s.items.length >= 2);
        setSequences(usable);
        const mode = p.config?.promptMode;
        setPromptMode(mode === 'TEXT' ? 'TEXT' : 'AUDIO');
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Could not load the program');
      }
    })();
    return () => { cancelled = true; };
  }, [programId]);

  const startRound = useCallback(async () => {
    if (!sequences || sequences.length === 0) return;
    setError(null);
    setDone(false);
    setOnBreak(false);
    setTapped([]);
    setStep(0);
    stopSpeaking();

    const chosen = sequences[Math.floor(Math.random() * sequences.length)];
    const positions = shuffle(chosen.items.map((_, i) => i));
    try {
      const { trialId: id } = await registerTrial(
        programId,
        learnerId,
        { label: chosen.label, tiles: positions.map((i) => chosen.items[i]) },
        { value: chosen.items.join(ORDER_SEPARATOR) },
      );
      setSeq(chosen);
      setOrder(positions);
      setTrialId(id);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not start the round');
    } finally {
      setFadingOut(false);
    }
  }, [sequences, programId, learnerId]);

  useEffect(() => { void startRound(); }, [startRound]);

  useEffect(() => {
    if (!settings.features.rewardTotal) return;
    let cancelled = false;
    void fetchRewardSummary(learnerId ?? null).then((s) => {
      if (!cancelled) setCollected(s);
    });
    return () => { cancelled = true; };
  }, [learnerId, settings.features.rewardTotal]);

  const playPrompt = useCallback(() => {
    if (!seq) return;
    if (promptMode === 'AUDIO' && canSpeak()) {
      speak(seq.label, { voiceName: settings.ttsVoice, rate: settings.ttsRate });
    }
  }, [seq, promptMode, settings.ttsVoice, settings.ttsRate]);

  useEffect(() => { playRef.current = playPrompt; }, [playPrompt]);

  useEffect(() => {
    if (!seq) return;
    const t = window.setTimeout(() => playRef.current(), 350);
    return () => window.clearTimeout(t);
  }, [seq, trialId]);

  useEffect(() => () => stopSpeaking(), []);

  useEffect(() => {
    if (!done) return;
    const transitionMs = settings.roundTransitionType === 'NONE' ? 0 : settings.roundTransitionMs;
    const fade = window.setTimeout(() => setFadingOut(true), FEEDBACK_HOLD_MS);
    const advance = window.setTimeout(() => { void startRound(); }, FEEDBACK_HOLD_MS + transitionMs);
    return () => {
      window.clearTimeout(fade);
      window.clearTimeout(advance);
    };
  }, [done, startRound, settings.roundTransitionMs, settings.roundTransitionType]);

  useEffect(() => {
    const session = sessionIdRef.current;
    return () => {
      void endSession(programId, session, answeredRef.current);
    };
  }, [programId]);

  /*
   * A WRONG TAP DOES NOTHING, AND IS NOT SENT.
   *
   * The same reasoning as errorless teaching on a board: an attempt recorded is
   * the error the technique exists to prevent. The trial is judged once, when
   * the order is complete, so what reaches the server is the order the learner
   * actually built rather than every false start along the way.
   */
  async function tap(position: number) {
    if (!seq || !trialId || done || tapped.includes(position)) return;
    if (order[position] !== step) return;

    const nextStep = step + 1;
    setTapped((t) => [...t, position]);
    setStep(nextStep);
    if (nextStep < seq.items.length) return;

    try {
      const verdict = await respondTrial(trialId, seq.items.join(ORDER_SEPARATOR));
      answeredRef.current = true;
      if (verdict.rewarded > 0) setEarnedNow((n) => n + verdict.rewarded);
      if (settings.correctPingEnabled) playCorrectPing();
      setDone(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not record that round');
    }
  }

  async function takeBreak() {
    if (!trialId) return;
    stopSpeaking();
    await api<{ recorded: boolean }>(`/games/rounds/${trialId}/break`, {
      method: 'POST',
      token: true,
    }).catch(() => undefined);
    setOnBreak(true);
  }

  const tilesGridClass = useMemo(() => gridClass(order.length), [order.length]);

  if (error) {
    return (
      <div className="card">
        <div className="error" role="alert">{error}</div>
      </div>
    );
  }
  if (sequences && sequences.length === 0) {
    return (
      <div className="card">
        <p>This program has no orders to learn yet.</p>
        <p className="field-hint">{KINDS.sequenceTap.describes} Edit it to add one.</p>
      </div>
    );
  }
  if (!seq) return null;

  if (onBreak) {
    return (
      <div className="card">
        <h2>Break time</h2>
        <p className="field-hint">
          Asking to stop is written down as asking to stop. It is not a wrong answer and it
          does not count against anything.
        </p>
        <div className="toolbar">
          <button className="btn" onClick={() => { void startRound(); }}>
            Ready to carry on
          </button>
        </div>
      </div>
    );
  }

  const effectiveMs = settings.roundTransitionType === 'NONE' ? 0 : settings.roundTransitionMs;
  const transitionStyle = { '--round-transition-ms': `${effectiveMs}ms` } as React.CSSProperties;
  const typeClass = `round-${settings.roundTransitionType.toLowerCase()}`;

  return (
    <>
      {settings.features.rewardTotal && collected?.enabled && (
        <div className="board-total">
          <span aria-hidden="true">
            {REWARD_ICONS[collected.icon as RewardIcon] ?? REWARD_ICONS.star}
          </span>
          <strong>{collected.total + earnedNow}</strong>
          <span>{collected.name}</span>
        </div>
      )}

      <div className="concept-prompt-text" aria-live="polite">{seq.label}</div>

      <div className="concept-sequence-row" aria-label="Order so far">
        {seq.items.map((it, i) => (
          <span key={i} className={`concept-seq-slot${i < step ? ' filled' : ''}`}>
            {i < step ? it : i + 1}
          </span>
        ))}
      </div>

      <div
        className={`tile-grid ${tilesGridClass} ${typeClass}${fadingOut ? ' fading-out' : ''}`}
        style={transitionStyle}
        role="group"
        aria-label="Sequence tiles"
      >
        {order.map((itemIndex, position) => {
          const used = tapped.includes(position);
          return (
            <button
              key={`${position}-${itemIndex}`}
              className={`tile${used ? ' correct' : ''}`}
              onClick={() => void tap(position)}
              disabled={used || done}
              aria-label={seq.items[itemIndex]}
            >
              {seq.items[itemIndex]}
            </button>
          );
        })}
      </div>

      {settings.features.assentBreak && !done && (
        <div className="toolbar break-bar">
          <button className="btn secondary" onClick={() => void takeBreak()}>
            I need a break
          </button>
        </div>
      )}
    </>
  );
}
